import { useCallback, useEffect, useRef, useState } from "react";

export default function useThrottle<T>(
  initialValue: T,
  interval: number
): [T, (value: T) => void] {
  const [throttledValue, setThrottledValue] = useState<T>(initialValue);

  const valueRef = useRef<T>(initialValue);
  const lastUpdateRef = useRef<number>(Date.now());
  const handleRef = useRef<ReturnType<typeof setTimeout>>(null);

  const setValue = useCallback(
    (value: T) => {
      valueRef.current = value;

      if (!handleRef.current) {
        const now = Date.now();
        const elapsed = now - lastUpdateRef.current;
        if (elapsed >= interval) {
          lastUpdateRef.current = now;
          setThrottledValue(valueRef.current);
        } else {
          handleRef.current = setTimeout(() => {
            handleRef.current = null;

            lastUpdateRef.current = Date.now();
            setThrottledValue(valueRef.current);
          }, interval - elapsed);
        }
      }
    },
    [interval]
  );

  useEffect(() => {
    return () => {
      if (handleRef.current) {
        clearTimeout(handleRef.current);
      }
    };
  }, []);

  return [throttledValue, setValue];
}
